import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { MatDialogRef } from '@angular/material/dialog'
import { NotificationService } from '@app/core/services/notification.service'
import { LoadingService } from '@app/core/state/loading/loading.service'
import { UpdateProfile } from '@app/user/state/user.model'
import { UserQuery } from '@app/user/state/user.query'
import { UserService } from '@app/user/state/user.service'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { RxwebValidators } from '@rxweb/reactive-form-validators'
import { Observable, of } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'

type UserProfileFormData = {
  email: FormControl<string>
  firstName: FormControl<string>
  lastName: FormControl<string>
  company: FormControl<string>
  oldPassword: FormControl<string | null>
  newPassword: FormControl<string | null>
  confirmPassword: FormControl<string | null>
}

@UntilDestroy()
@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UserProfileComponent implements OnInit {
  profileForm: FormGroup<UserProfileFormData>

  serverError: boolean

  isLoading$: Observable<boolean>

  isFormSubmitted: boolean

  constructor(
    private userService: UserService,
    private userQuery: UserQuery,
    private dialogRef: MatDialogRef<UserProfileComponent>,
    private loadingService: LoadingService,
    private notificationService: NotificationService,
    formBuilder: FormBuilder,
  ) {
    this.profileForm = formBuilder.group({
      email: [{ value: '', disabled: true }],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      company: ['', Validators.required],
      oldPassword: [
        '',
        RxwebValidators.required({
          conditionalExpression: 'form => form.newPassword !== "" || form.confirmPassword !== ""',
        }),
      ],
      newPassword: [
        '',
        [
          RxwebValidators.required({
            conditionalExpression: 'form => form.oldPassword !== ""',
          }),
        ],
      ],
      confirmPassword: [
        '',
        RxwebValidators.compare({
          fieldName: 'newPassword',
        }),
      ],
    })
  }

  ngOnInit(): void {
    this.isFormSubmitted = false
    this.isLoading$ = this.loadingService.isLoading$

    this.userQuery.profile$
      .pipe(
        switchMap(profile => {
          return profile != null ? of(profile) : this.userService.getProfile()
        }),
        map(profile => {
          return {
            email: profile.Email,
            firstName: profile.FirstName,
            lastName: profile.LastName,
            company: profile.Company,
          }
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: userProfile => {
          this.profileForm.patchValue(userProfile)
        },
      })
  }

  save(): void {
    this.isFormSubmitted = true
    const formData = this.profileForm.value
    const data = {
      FirstName: formData.firstName,
      LastName: formData.lastName,
      Company: formData.company,
      OldPassword: formData.oldPassword === '' ? null : formData.oldPassword,
      NewPassword: formData.newPassword === '' ? null : formData.newPassword,
      ConfirmPassword: formData.confirmPassword === '' ? null : formData.confirmPassword,
    } as UpdateProfile

    this.userService
      .saveProfile(data)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.dialogRef.close()
          this.notificationService.success('UI.Profile.Saved')
        },
        error: () => {
          this.isFormSubmitted = false
          this.setError(true)
        },
      })
  }

  setError(show: boolean): void {
    this.serverError = show
  }

  get firstName(): AbstractControl {
    return this.profileForm.get('firstName')
  }

  get lastName(): AbstractControl {
    return this.profileForm.get('lastName')
  }

  get company(): AbstractControl {
    return this.profileForm.get('company')
  }

  get oldPassword(): AbstractControl {
    return this.profileForm.get('oldPassword')
  }

  get newPassword(): AbstractControl {
    return this.profileForm.get('newPassword')
  }

  get confirmPassword(): AbstractControl {
    return this.profileForm.get('confirmPassword')
  }
}
